// ============================================================
// regroupementFeuilles — plusieurs FEUILLES, une seule PIÈCE.
// ------------------------------------------------------------
// Philippe, 29 octobre 2026 (devis 2026-10-046) : un plan TRASIS sur deux
// ou trois feuilles (« Page : 1/3, 2/3, 3/3 ») donnait deux ou trois
// pièces incomplètes — la règle « une page = une pièce » prenait chaque
// feuille pour une pièce. 15 pièces en trop sur 49.
//
// ── LA RÈGLE, DÉCIDÉE PAR THIBAUD ──
//
// > Une erreur de regroupement (deux pièces différentes fusionnées) est
// > PIRE que l'erreur d'avant (une pièce coupée en deux) : elle ne se
// > voit pas.
//
// Donc le programme ne regroupe TOUT SEUL que les cas certains. Sont
// exigés, tous, non vides et identiques :
//
//   même DOCUMENT · même RÉFÉRENCE · même INDICE · même DÉSIGNATION ·
//   même MATIÈRE · un repère de feuille « x/y » cohérent (même y > 1,
//   x tous différents)
//
// Le « numéro de pièce ou de repère » s'ajoute « quand il existe » : la
// lecture ne le relève pas aujourd'hui, il n'y a donc rien à comparer.
//
// Dès qu'un élément manque ou diffère, on ne regroupe PAS : on PROPOSE,
// en disant ce qui diffère, et le deviseur décide. Deux documents
// distincts ne sont jamais regroupés automatiquement.
//
// Tout est ici du calcul sur des chaînes déjà lues : aucun modèle (§2bis,
// règle 1). Pur, testé, importé par l'API.
// ============================================================

import { z } from 'zod';

/** Les critères, dans l'ordre où l'écran les nomme. */
export const CRITERES_REGROUPEMENT = [
  { cle: 'reference', libelle: 'référence' },
  { cle: 'indice', libelle: 'indice' },
  { cle: 'designation', libelle: 'désignation' },
  { cle: 'matiere', libelle: 'matière' },
];

/** Comparaison sans casse, sans accents, espaces resserrés. */
export function normaliser(s) {
  return String(s ?? '')
    .normalize('NFD').replace(/\p{Diacritic}/gu, '')
    .toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * « Page : 2/3 », « Sheet 2 of 4 », « Foglio 1 Di 1 », « Feuille 2 sur 3 »
 * → `{ x: 2, y: 3 }`. Rien de reconnu → `null` : on ne devine pas.
 */
export function lirePlanche(texte) {
  const m = String(texte ?? '').match(/(\d+)\s*(?:\/|of|de|di|sur|von)\s*(\d+)/i);
  if (!m) return null;
  const x = Number(m[1]);
  const y = Number(m[2]);
  if (!(x >= 1 && y >= 1 && x <= y)) return null;
  return { x, y };
}

/** Ce que la lecture d'une feuille dit de son cartouche. */
export function cartoucheDesFaits(facts = {}) {
  return {
    reference: facts.reference ?? null,
    indice: facts.indice ?? null,
    designation: facts.designation ?? null,
    matiere: facts.material_label ?? null,
    planche: lirePlanche(facts.planche),
  };
}

/**
 * Compare deux cartouches. `identiques` : les critères présents et égaux ;
 * `ecarts` : ceux qui diffèrent ou manquent d'un côté, avec les deux valeurs.
 */
export function comparerCartouches(a, b) {
  const identiques = [];
  const ecarts = [];
  for (const { cle, libelle } of CRITERES_REGROUPEMENT) {
    const va = normaliser(a[cle]);
    const vb = normaliser(b[cle]);
    if (va && va === vb) identiques.push(libelle);
    else ecarts.push({ critere: libelle, a: a[cle] ?? null, b: b[cle] ?? null });
  }
  return { identiques, ecarts };
}

const cleCertaine = (f) => {
  const c = f.cartouche;
  if (!f.document_id || !c.planche || c.planche.y < 2) return null;
  const champs = CRITERES_REGROUPEMENT.map(({ cle }) => normaliser(c[cle]));
  if (champs.some((v) => !v)) return null;
  return [f.document_id, ...champs, c.planche.y].join('\u0001');
};

/**
 * Les regroupements AUTOMATIQUES d'un ensemble de feuilles lues.
 *
 * @param feuilles `[{ file_id, document_id, page_number, cartouche }]` —
 *   seulement des feuilles reconnues comme plans, sans pièce, non refusées.
 * @returns `[{ feuilles: [file_id…] (dans l'ordre x), raison }]`, seulement
 *   les groupes d'au moins deux feuilles.
 */
export function regroupementsAutomatiques(feuilles) {
  const paquets = new Map();
  for (const f of feuilles) {
    const cle = cleCertaine(f);
    if (!cle) continue;
    if (!paquets.has(cle)) paquets.set(cle, []);
    paquets.get(cle).push(f);
  }
  const groupes = [];
  for (const paquet of paquets.values()) {
    if (paquet.length < 2) continue;
    // Deux feuilles « 2/3 » dans le même paquet : le repère ne départage
    // plus rien. Ce n'est plus un cas certain.
    const xs = paquet.map((f) => f.cartouche.planche.x);
    if (new Set(xs).size !== xs.length) continue;
    const tries = [...paquet].sort((a, b) => a.cartouche.planche.x - b.cartouche.planche.x);
    const { y } = tries[0].cartouche.planche;
    groupes.push({
      feuilles: tries.map((f) => f.file_id),
      raison: `même document, même référence, même indice, même désignation, même matière ; `
        + `feuilles ${tries.map((f) => f.cartouche.planche.x).join(', ')} sur ${y}`,
    });
  }
  return groupes;
}

/**
 * Les regroupements à PROPOSER entre des pièces déjà formées : même
 * référence (tous documents confondus), ou même désignation dans un même
 * document — sans remplir toutes les conditions du cas certain.
 *
 * @param unites `[{ piece_id, documents: [id…], cartouche, refuse }]`
 * @returns `[{ pieces: [piece_id…], identiques, ecarts }]`
 */
export function propositionsDeRegroupement(unites) {
  const n = unites.length;
  const parent = unites.map((_, i) => i);
  const racine = (i) => (parent[i] === i ? i : (parent[i] = racine(parent[i])));
  const lier = (i, j) => { parent[racine(i)] = racine(j); };
  const memeDocument = (a, b) => a.documents.some((d) => b.documents.includes(d));

  for (let i = 0; i < n; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      const a = unites[i].cartouche;
      const b = unites[j].cartouche;
      const ref = normaliser(a.reference) && normaliser(a.reference) === normaliser(b.reference);
      const des = normaliser(a.designation) && normaliser(a.designation) === normaliser(b.designation);
      if (ref || (des && memeDocument(unites[i], unites[j]))) lier(i, j);
    }
  }
  const paquets = new Map();
  unites.forEach((u, i) => {
    const r = racine(i);
    if (!paquets.has(r)) paquets.set(r, []);
    paquets.get(r).push(u);
  });

  const propositions = [];
  for (const paquet of paquets.values()) {
    if (paquet.length < 2) continue;
    // Le deviseur a déjà dit non (séparé, ou proposition ignorée) : on ne
    // repose pas la question tant qu'il ne regroupe pas lui-même.
    if (paquet.every((u) => u.refuse)) continue;
    const { identiques, ecarts } = comparerCartouches(paquet[0].cartouche, paquet[1].cartouche);
    for (const u of paquet.slice(2)) {
      for (const e of comparerCartouches(paquet[0].cartouche, u.cartouche).ecarts) {
        if (!ecarts.some((x) => x.critere === e.critere)) ecarts.push(e);
      }
    }
    const docs = new Set(paquet.flatMap((u) => u.documents));
    if (docs.size > 1) ecarts.unshift({ critere: 'document', a: null, b: null, note: 'documents distincts' });
    propositions.push({
      pieces: paquet.map((u) => u.piece_id),
      identiques: identiques.filter((c) => !ecarts.some((e) => e.critere === c)),
      ecarts,
    });
  }
  return propositions;
}

// ── Les corps des gestes manuels (validés côté serveur) ─────────

/** « Regrouper » : au moins deux pièces, et l'acceptation de perdre le travail fait dessus. */
export const regroupementSchema = z.object({
  pieces: z.array(z.number().int().positive()).min(2, 'Choisissez au moins deux pièces à regrouper.').max(60),
  confirmer_perte: z.boolean().optional().default(false),
}).strict();

/** « Ignorer » une proposition : le deviseur dit non, on ne repose plus la question. */
export const ignorerRegroupementSchema = z.object({
  pieces: z.array(z.number().int().positive()).min(2).max(60),
}).strict();
